import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { tasksApi } from '@/utils/api';
import InlineTaskPanel from '@/components/InlineTaskPanel';
import { toast } from 'sonner';

const TaskDrawerContext = createContext(null);

export function useTaskDrawer() {
  const ctx = useContext(TaskDrawerContext);
  if (!ctx) throw new Error('useTaskDrawer must be used within TaskDrawerProvider');
  return ctx;
}

export function TaskDrawerProvider({ children }) {
  const [state, setState] = useState(null); // { taskId, onChanged }

  const openTaskDrawer = useCallback((taskId, opts = {}) => {
    if (!taskId) return;
    setState({ taskId, onChanged: opts.onChanged });
  }, []);
  const closeTaskDrawer = useCallback(() => setState(null), []);

  return (
    <TaskDrawerContext.Provider value={{ openTaskDrawer, closeTaskDrawer }}>
      {children}
      <GlobalTaskDrawer
        taskId={state?.taskId || null}
        onChanged={state?.onChanged}
        onClose={closeTaskDrawer}
      />
    </TaskDrawerContext.Provider>
  );
}

function GlobalTaskDrawer({ taskId, onChanged, onClose }) {
  const [task, setTask] = useState(null);

  const fetchTask = useCallback(async () => {
    if (!taskId) { setTask(null); return; }
    try {
      const res = await tasksApi.get(taskId);
      setTask(res.data);
    } catch {
      setTask(null);
      toast.error('Could not load task');
    }
  }, [taskId]);

  useEffect(() => { fetchTask(); }, [fetchTask]);

  // Escape to close
  useEffect(() => {
    if (!taskId) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [taskId, onClose]);

  const refresh = useCallback(async () => {
    await fetchTask();
    onChanged?.();
  }, [fetchTask, onChanged]);

  const open = !!taskId;

  return (
    <>
      <div className="detail-bd" style={{ display: open ? 'block' : 'none' }} onClick={onClose} data-testid="task-detail-backdrop" />
      <div className={`detail${open ? ' open' : ''}`} data-testid="task-detail-drawer">
        {open && task && (
          <InlineTaskPanel
            task={task}
            canAssign={!!(task.can_assign ?? (task.is_shared === false))}
            onChanged={refresh}
            onClose={onClose}
          />
        )}
      </div>
    </>
  );
}
